import React, { useEffect } from 'react';
import useSensorStore from '../store/sensorStore';
import useEditSensor from '../hooks/useEditSensor';
import useFetchSensorById from '../hooks/useFetchSensorById';
import useFetchSensorInstallationHistory from '../hooks/useFetchSensorInstallationHistory';
import useFetchSensorCommentHistory from '../hooks/useFetchSensorCommentHistory'
import useMediaQuery from '@mui/material/useMediaQuery';

import CommentsHistory from '../components/CommentsHistoryTable';
import InstallationHistoryTable from '../components/InstallationHistoryTable';
import SensorForm from '../components/SensorForm';
import PageLoader from '../components/PageLoader';
import Box from '@mui/material/Box';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { MOBILE_MEDIA_QUERY_WIDTH } from '../utils/constants';
import theme from '../theme';

const sortComments = (comments: CommentsHistory[]) => comments.sort((a, b) => {
  const dateA = new Date(a.createdOn);
  const dateB = new Date(b.createdOn);
  return dateB.getTime() - dateA.getTime(); // Newest first
});

export default function EditSensor() {
  const { fetchSensor, loading } = useFetchSensorById();
  const { fetchSensorInstallationHistory, loading: installationHistoryLoading } = useFetchSensorInstallationHistory();
  const { fetchSensorCommentHistory, loading: commentHistoryLoading } = useFetchSensorCommentHistory();
  const { editSensor, loading: submitLoading } = useEditSensor();

  const { editSensorData, installationHistory, commentHistory } = useSensorStore();

  useEffect(() => {
    fetchSensor();
    fetchSensorInstallationHistory();
    fetchSensorCommentHistory();
  }, []);

  const displayComments = [...commentHistory];

  const generalComments = displayComments.filter((c) => !c.installationId);


  const sortedGeneralComments = sortComments(generalComments);
  const sortedAllComments = sortComments(displayComments);

  const mostRecentComment = sortedAllComments[0]?.comment;

  const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY_WIDTH);
  const boxMaxWidth = isMobile ? 'calc(100% - 32px)' : '1200px';

  return (loading || installationHistoryLoading || commentHistoryLoading) ? (
    <PageLoader />
  ) : (
    <Box sx={{ maxWidth: boxMaxWidth, margin: '0 auto', marginTop: '20px', width: '100%' }}>
      <Accordion defaultExpanded>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="sensor-form-content"
          id="sensor-form-header"
          sx={{ backgroundColor: theme.palette.grey[100] }}
        >
          <Typography variant="h6">Sensor Info</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ overflowX: 'hidden' }}>
          <SensorForm
            onSubmitRequest={editSensor}
            submitLoading={submitLoading}
            editData={editSensorData!}
            mostRecentComment={mostRecentComment}
          />
        </AccordionDetails>
      </Accordion>

      {!!installationHistory.length && (
        <Accordion defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="installation-history-content"
            id="installation-history-header"
            sx={{ backgroundColor: theme.palette.grey[100] }}
          >
            <Typography variant="h6">Installation History</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ overflowX: 'hidden' }}>
            <InstallationHistoryTable installationHistory={installationHistory} commentHistory={sortedAllComments} />
          </AccordionDetails>
        </Accordion>
      )}

      <Accordion defaultExpanded>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="comments-history-content"
          id="comments-history-header"
          sx={{ backgroundColor: theme.palette.grey[100] }}
        >
          <Typography variant="h6">Comments History</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ overflowX: 'hidden' }}>
          <CommentsHistory commentHistory={sortedGeneralComments} />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};
